import { useEffect, useState } from 'react';

import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { REPORT } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import * as S from './AggregatorAging.styled';

const AggregatorAgingSummary = () => {
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const { getDate } = useLocale();

	const [weeklyAnalysis, setWeeklyAnalysis] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	const getWeeklyAnalysisData = async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(REPORT.DELIVERY_COMPANY.GET_AGGREGATOR_AGING_WEEKLY_ANALYSIS, {
				totalRecord: false
			});
			setWeeklyAnalysis(response.data.data);
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getWeeklyAnalysisData();
	}, []);

	return (
		<S.Container className="pt-4">
			<Typography variant="p1" fontWeight="700">
				weekOverWeekSummary
			</Typography>

			{isLoading
				? [...Array(3)].map((_, index) => (
						<S.SummaryCardWrapper key={`skeleton_${index}`}>
							<Skeleton width="15rem" height="2rem" />
							<S.SummaryCardContainer>
								<S.SummaryCardArea className="center">
									<Skeleton height="2rem" width="12rem" />
									<Skeleton height="4rem" width="10rem" />
								</S.SummaryCardArea>
								<S.SummaryCardArea className="center">
									<Skeleton height="2rem" width="12rem" />
									<Skeleton height="4rem" width="10rem" />
								</S.SummaryCardArea>
								<S.SummaryCardArea className="center">
									<Skeleton height="2rem" width="12rem" />
									<Skeleton height="4rem" width="10rem" />
								</S.SummaryCardArea>
							</S.SummaryCardContainer>
						</S.SummaryCardWrapper>
					))
				: weeklyAnalysis?.map((item) => (
						<WeekSummaryCard
							key={item.week}
							title={`week ${item.week} (${getDate(item.startWeek)} - ${getDate(item.endWeek)})`}
							pickupAging={{
								number: item?.pickupAgingDays,
								percentage: item?.pickupChangePercentage,
								isIncreased: item?.isPickupIncreased
							}}
							deliveryAging={{
								number: item?.deliveryAgingDays,
								percentage: item?.deliveryChangePercentage,
								isIncreased: item?.isDeliveryIncreased
							}}
							totalAging={{
								number: item?.totalAgingDays,
								percentage: item?.totalChangePercentage,
								isIncreased: item?.isTotalIncreased
							}}
						/>
					))}
		</S.Container>
	);
};

const WeekSummaryCard = ({ title, pickupAging, deliveryAging, totalAging }) => (
	<S.SummaryCardWrapper>
		<Typography variant="p2">{title}</Typography>
		<S.SummaryCardContainer>
			<S.SummaryCardArea>
				<Typography>pickupAging</Typography>
				<Typography variant="p1" className="card-number-text">
					{pickupAging?.number ?? '-'}
					{pickupAging?.percentage && (
						<Typography
							variant="caption"
							className="card-percentage-text"
							color={pickupAging?.isIncreased ? 'var(--green)' : 'var(--red-hover)'}>
							{pickupAging.percentage}%
						</Typography>
					)}
				</Typography>
			</S.SummaryCardArea>
			<S.SummaryCardArea>
				<Typography>deliveryAging</Typography>
				<Typography variant="p1" className="card-number-text">
					{deliveryAging?.number ?? '-'}
					{deliveryAging?.percentage && (
						<Typography
							variant="caption"
							className="card-percentage-text"
							color={deliveryAging?.isIncreased ? 'var(--green)' : 'var(--red-hover)'}>
							{deliveryAging.percentage}%
						</Typography>
					)}
				</Typography>
			</S.SummaryCardArea>
			<S.SummaryCardArea>
				<Typography>totalAging</Typography>
				<Typography variant="p1" className="card-number-text">
					{totalAging?.number ?? '-'}
					{totalAging?.percentage && (
						<Typography
							variant="caption"
							className="card-percentage-text"
							color={totalAging?.isIncreased ? 'var(--green)' : 'var(--red-hover)'}>
							{totalAging.percentage}%
						</Typography>
					)}
				</Typography>
			</S.SummaryCardArea>
		</S.SummaryCardContainer>
	</S.SummaryCardWrapper>
);

export default AggregatorAgingSummary;
